import React from 'react';
import { Link } from 'react-router-dom';
import { SubPageLayout } from '../components/SubPageLayout';
import { useSeo } from '../lib/seo';

export default function Datenschutz() {
  useSeo({
    title: 'Datenschutzerklärung | Smart Post Solution',
    description: 'Datenschutzerklärung von Smart Post Solution: Informationen zu Datenerfassung, Chatbot, Kontaktformular, Hosting und Ihren Rechten gemäß DSGVO.',
    canonical: 'https://smart-post-solution.de/datenschutz',
  });

  return (
    <SubPageLayout>
      <div className="max-w-3xl mx-auto px-6 lg:px-12 py-16 lg:py-24" data-testid="datenschutz-page">
        <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-10">Datenschutzerklärung</h1>
        <div className="space-y-8 text-slate-700">
          <section>
            <h2 className="font-semibold text-lg mb-2 text-slate-900">1. Datenschutz auf einen Blick</h2>
            <h3 className="font-semibold mb-2">Allgemeine Hinweise</h3>
            <p className="text-sm leading-relaxed mb-4">
              Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten
              passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie
              persönlich identifiziert werden können.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-lg mb-2 text-slate-900">2. Datenerfassung auf dieser Website</h2>
            <h3 className="font-semibold mb-2">Wer ist verantwortlich für die Datenerfassung auf dieser Website?</h3>
            <p className="text-sm leading-relaxed mb-4">
              Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten
              können Sie dem Impressum dieser Website entnehmen.
            </p>

            <h3 className="font-semibold mb-2">Wie erfassen wir Ihre Daten?</h3>
            <p className="text-sm leading-relaxed mb-4">
              Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich z. B.
              um Daten handeln, die Sie in unser Kontaktformular oder im Chatbot eingeben.
            </p>
            <p className="text-sm leading-relaxed mb-4">
              Andere Daten werden automatisch oder nach Ihrer Einwilligung beim Besuch der Website durch unsere
              IT-Systeme erfasst. Das sind vor allem technische Daten (z. B. Internetbrowser, Betriebssystem oder
              Uhrzeit des Seitenaufrufs).
            </p>

            <h3 className="font-semibold mb-2">Wofür nutzen wir Ihre Daten?</h3>
            <p className="text-sm leading-relaxed mb-4">
              Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten.
              Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden.
            </p>

            <h3 className="font-semibold mb-2">Welche Rechte haben Sie bezüglich Ihrer Daten?</h3>
            <p className="text-sm leading-relaxed mb-4">
              Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer
              gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung oder
              Löschung dieser Daten zu verlangen.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-lg mb-2 text-slate-900">3. Chatbot und Kontaktformulare</h2>
            <h3 className="font-semibold mb-2">Art und Umfang der Datenverarbeitung</h3>
            <p className="text-sm leading-relaxed mb-4">
              Unsere Website verwendet einen Chatbot zur Beantwortung Ihrer Anfragen. Dabei werden folgende Daten erhoben:
            </p>
            <ul className="list-disc list-inside text-sm space-y-2 mb-4">
              <li>Chat-Nachrichten und deren Inhalt</li>
              <li>Zeitstempel der Nachrichten</li>
              <li>Session-ID zur Zuordnung des Gesprächsverlaufs</li>
              <li>Bei Lead-Anfragen: Name, E-Mail, Firma, Telefonnummer (freiwillig)</li>
            </ul>

            <h3 className="font-semibold mb-2">KI-gestützte Verarbeitung</h3>
            <p className="text-sm leading-relaxed mb-4">
              Der Chatbot verwendet zur Beantwortung Ihrer Anfragen ein KI-Sprachmodell (Large Language Model). Ihre Eingaben werden zur Verarbeitung an einen externen KI-Dienst übermittelt. Es werden keine personenbezogenen Daten dauerhaft beim KI-Anbieter gespeichert. Bitte geben Sie im Chat keine sensiblen personenbezogenen Daten ein.
            </p>

            <h3 className="font-semibold mb-2">Rechtsgrundlage</h3>
            <p className="text-sm leading-relaxed mb-4">
              Die Verarbeitung erfolgt auf Grundlage Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO) bzw. zur Durchführung vorvertraglicher Maßnahmen (Art. 6 Abs. 1 lit. b DSGVO).
            </p>

            <h3 className="font-semibold mb-2">Speicherdauer</h3>
            <p className="text-sm leading-relaxed mb-4">
              Chat-Verläufe werden für 90 Tage gespeichert und anschließend automatisch gelöscht. Lead-Anfragen werden bis zur vollständigen Bearbeitung Ihrer Anfrage gespeichert.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-lg mb-2 text-slate-900">4. Hosting</h2>
            <p className="text-sm leading-relaxed mb-4">
              Wir hosten die Inhalte unserer Website bei externen Dienstleistern. Die personenbezogenen Daten, die
              auf dieser Website erfasst werden, werden auf den Servern des Hosters gespeichert.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-lg mb-2 text-slate-900">5. Technisch notwendige Cookies</h2>
            <p className="text-sm leading-relaxed mb-4">
              Diese Website verwendet ausschließlich technisch notwendige Cookies, die für den Betrieb des Chatbots und die Speicherung Ihrer Session erforderlich sind. Diese Cookies erfordern keine Einwilligung gemäß DSGVO.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-lg mb-2 text-slate-900">6. Kontakt</h2>
            <p className="text-sm leading-relaxed">
              Bei Fragen zum Datenschutz wenden Sie sich bitte über unser{' '}
              <Link to="/#kontakt" className="text-slate-700 hover:text-slate-900 underline">Kontaktformular</Link> an uns.
              Alternativ per E-Mail an <span className="text-slate-600">info [at] smart-post-solution [punkt] de</span>
              {' '}(verschleierte Schreibweise zum Schutz vor Spam-Bots).
            </p>
          </section>
        </div>
      </div>
    </SubPageLayout>
  );
}
